import React, { useState, useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import RichTextEditor from '../../components/RichTextEditor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Plus, 
  Pencil, 
  Trash,
  Eye,
  Article,
  Calendar,
  MagnifyingGlass
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const { currentStore } = useStore();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    meta_title: '',
    meta_description: '',
    status: 'draft',
    is_featured: false
  });

  const fetchPosts = async () => {
    if (!currentStore) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/stores/${currentStore.id}/blog`, {
        withCredentials: true
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [currentStore]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'title' ? { slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPost) {
        await axios.put(`${API_URL}/api/stores/${currentStore.id}/blog/${editingPost.id}`, formData, {
          withCredentials: true
        });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API_URL}/api/stores/${currentStore.id}/blog`, formData, {
          withCredentials: true
        });
        toast.success('Article créé');
      }
      setModalOpen(false);
      setEditingPost(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        featured_image: '',
        meta_title: '',
        meta_description: '',
        status: 'draft',
        is_featured: false
      });
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      status: post.status,
      is_featured: post.is_featured || false
    });
    setModalOpen(true);
  };

  const handleDelete = async (postId) => {
    if (!confirm('Supprimer cet article ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/stores/${currentStore.id}/blog/${postId}`, {
        withCredentials: true
      });
      toast.success('Article supprimé');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (!currentStore) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Sélectionnez une boutique pour gérer le blog</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="blog-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-cabinet text-3xl font-bold tracking-tight">Blog SEO</h1>
          <p className="text-slate-500 mt-1">Créez du contenu pour améliorer votre référencement</p>
        </div>
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#0052FF] hover:bg-[#0040CC]"
              onClick={() => {
                setEditingPost(null);
                setFormData({
                  title: '',
                  slug: '',
                  content: '',
                  excerpt: '',
                  featured_image: '',
                  meta_title: '',
                  meta_description: '',
                  status: 'draft'
                });
              }}
              data-testid="new-post-btn"
            >
              <Plus className="mr-2" size={18} />
              Nouvel article
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Modifier l\'article' : 'Nouvel article'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    data-testid="post-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    data-testid="post-slug"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Extrait</Label>
                <Textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Résumé court de l'article..."
                  data-testid="post-excerpt"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Contenu *</Label>
                <RichTextEditor
                  content={formData.content || ''}
                  onChange={val => setFormData(p => ({ ...p, content: val }))}
                  placeholder="Rédigez votre article..."
                  minHeight={300}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Image à la une (URL)</Label>
                <Input
                  name="featured_image"
                  value={formData.featured_image}
                  onChange={handleInputChange}
                  placeholder="https://..."
                  data-testid="post-image"
                />
              </div>

              <div className="flex items-center gap-3 py-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={formData.is_featured}
                  onChange={e => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 accent-[#0052FF]"
                  data-testid="post-featured-toggle"
                />
                <Label htmlFor="is_featured" className="cursor-pointer">
                  Mis en avant sur la page d'accueil
                </Label>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MagnifyingGlass size={18} />
                  SEO
                </h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Meta Title</Label>
                    <Input
                      name="meta_title"
                      value={formData.meta_title}
                      onChange={handleInputChange}
                      placeholder="Titre pour les moteurs de recherche"
                      data-testid="post-meta-title"
                    />
                    <p className="text-xs text-slate-500">{formData.meta_title?.length || 0}/60 caractères</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Meta Description</Label>
                    <Textarea
                      name="meta_description"
                      value={formData.meta_description}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder="Description pour les moteurs de recherche"
                      data-testid="post-meta-desc"
                    />
                    <p className="text-xs text-slate-500">{formData.meta_description?.length || 0}/160 caractères</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="border rounded-lg px-3 py-2"
                >
                  <option value="draft">Brouillon</option>
                  <option value="published">Publié</option>
                </select>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-post">
                    {editingPost ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Posts Table */}
      <Card className="dashboard-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF] mx-auto"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="p-12 text-center">
              <Article size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-2">Aucun article pour le moment</p>
              <p className="text-sm text-slate-400">Créez votre premier article de blog pour améliorer votre SEO</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Article</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post.id} data-testid={`post-row-${post.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-4">
                        {post.featured_image && (
                          <img 
                            src={post.featured_image} 
                            alt="" 
                            className="w-16 h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="font-medium">{post.title}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-slate-500">/{post.slug}</p>
                            {post.is_featured && <Badge className="bg-amber-100 text-amber-800 text-[10px] px-1.5">Mis en avant</Badge>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                        {post.status === 'published' ? 'Publié' : 'Brouillon'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar size={14} />
                        {new Date(post.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(post)}>
                          <Pencil size={18} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(post.id)}>
                          <Trash size={18} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlogPage;
